/**
 * What a technician can run on the tool they are standing at - the ids, the
 * menu and the marker.
 *
 * The other kind of answer. A scene (demos.ts) shows how something WORKS in
 * general; an experiment runs the machine in front of them and reports what
 * came out. Ask Sarah what raising the bias would do and there is no honest
 * sentence she can say - it is an experiment, and it has to be run - but she
 * is standing at the tool that runs it.
 *
 * METADATA ONLY, the same split demos.ts makes and for the same reason: the
 * brief, the wire and the check harness need to know WHAT can be run, and
 * none of them should drag a solver in to find out. `experimentRunner.ts`
 * holds the half that fetches catalogues and builds panels; `npm run talk`
 * asserts the two still name the same tools.
 */

/** One experiment, as the brief names it: what to ask for, and what it does. */
export interface ExperimentMenuItem {
  id: string
  /** The question that runs it, in the words the router matches. */
  example: string
  summary: string
}

/**
 * The tools with an experiment catalogue behind them.
 *
 * Only the tools somebody stands at. A module with intents and nobody at its
 * HMI has no conversation to serve, and listing it here would fetch physics
 * for a technician who cannot be spoken to.
 */
export const EXPERIMENT_TOOLS: readonly string[] = ['etch', 'cmp', 'litho', 'thermal', 'implant', 'deposition', 'metrology', 'epi', 'probe', 'euv', 'ebeam', 'quantum']

/** Whether anything can be run at this tool at all. */
export const hasExperiments = (toolId: string | null): boolean =>
  !!toolId && EXPERIMENT_TOOLS.includes(toolId)

/** Catalogues already fetched, by tool id. Ids and text only, no solver. */
const menus = new Map<string, ExperimentMenuItem[]>()

/** Filled by `warmExperiments` once a catalogue chunk lands. */
export function rememberMenu(toolId: string, items: ExperimentMenuItem[]): void {
  menus.set(toolId, items)
}

export const menuKnown = (toolId: string): boolean => menus.has(toolId)

/**
 * What the brief lists.
 *
 * Empty until the catalogue lands, and for tools with none - which is the
 * right failure while a chunk is in the air: the technician simply does not
 * offer to run anything, and an offer that cannot be honoured is worse than
 * no offer.
 */
export const experimentsFor = (toolId: string | null): ExperimentMenuItem[] =>
  (toolId && menus.get(toolId)) || []

/**
 * The marker a technician ends a reply with when they are going to run
 * something: `[[run: why is the edge etching faster than the centre]]`.
 *
 * The text inside is the question AS THE TECHNICIAN WOULD PUT IT, and that is
 * the point of letting them write it rather than passing the student's line
 * straight through. The assistant's catalogue is written in the words a
 * process engineer uses; a student says "the edges look burnt". Translating
 * one into the other is what somebody who works here is for, and it is not a
 * physics claim - whatever they write still has to route to a registered
 * intent, or the assistant says it cannot run that and shows the menu.
 *
 * A bare `[[run]]` runs the student's own question. That is why the empty
 * string is a real answer here and `undefined` is the "no": one means run
 * what they asked, the other means run nothing.
 */
const RUN = /\[\[\s*run\s*(?::\s*([^\]]*?))?\s*\]\]/gi

export function takeRun(text: string, allowed: boolean): { text: string; run?: string } {
  let run: string | undefined
  const stripped = text
    .replace(RUN, (_match, asked?: string) => {
      // Stripped even where running is not allowed - the line is about to be
      // spoken, and brackets are not speech.
      if (allowed && run === undefined) run = (asked ?? '').trim()
      return ''
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return run === undefined ? { text: stripped } : { text: stripped, run }
}
